import { __awaiter, __rest } from '../node_modules/tslib/tslib.es6.js';
import _get from 'lodash/get';
import mixpanel from 'mixpanel-browser';
import queryString from 'qs';
import BrandclubStoreConfigUtils from '../utils/brandclubStoreConfigUtils.js';
import { AnalyticsTrackingEvent } from './events.js';
import { enableDebug, track as track$1 } from './externalTracking.js';

const deferredPromise = () => {
    let resolve = () => void 0;
    let reject = () => void 0;
    let isDone = false;
    const p = new Promise((r, e) => {
        resolve = (v) => {
            isDone = true;
            r(v);
        };
        reject = (err) => {
            isDone = true;
            e(err);
        };
    });
    return { p, resolve, reject, isDone: () => isDone };
};
let isInitialized = false;
const __PROD__ = BrandclubStoreConfigUtils.getAppStage() === 'prod';
const DEBUG = !__PROD__ && false;
if (DEBUG) {
    enableDebug();
}
const currentVisitorTracking = deferredPromise();
function setCurrentVisitorTracking(visitorTracking) {
    if (visitorTracking && visitorTracking.uniqueVisitorId) {
        if (DEBUG) {
            // eslint-disable-next-line no-console
            console.log('[DEBUG] resolving ', visitorTracking);
        }
        currentVisitorTracking.resolve(visitorTracking);
    }
}
function track(eventName, eventProps, options) {
    return __awaiter(this, void 0, void 0, function* () {
        // Sends event to mixpanel
        const { location } = window;
        const params = queryString.parse(location.search, {
            ignoreQueryPrefix: true,
            arrayLimit: 100,
        });
        const pathName = _get(location.pathname.split('/'), '1');
        const eventNameForService = `${eventName}`;
        const _a = eventProps || {}, { brandPixels } = _a, rest = __rest(_a, ["brandPixels"]);
        const paramsForService = Object.assign(Object.assign(Object.assign({}, rest), params), { pathName: pathName === null || pathName === void 0 ? void 0 : pathName.toLowerCase(), referrer: document.referrer, retailerId: (rest === null || rest === void 0 ? void 0 : rest.retailerId) || params.rid });
        try {
            yield track$1(eventNameForService, paramsForService, brandPixels);
        }
        catch (e) {
            console.error(`Error sending event (${eventNameForService}) to external tracking: ${e.message}`);
        }
        if (DEBUG) {
            // eslint-disable-next-line no-console
            console.log(`[DEBUG] sending '${eventNameForService}' with params: `, paramsForService);
        }
        try {
            if (!isInitialized) {
                initializeMixpanel();
            }
            if (isInitialized) {
                if (DEBUG) {
                    // eslint-disable-next-line no-console
                    console.log(`[DEBUG] Sending ${eventNameForService} to Mixpanel`);
                }
                const visitorTracking = yield currentVisitorTracking.p;
                return new Promise((resolve, reject) => {
                    var _a, _b, _c, _d, _e, _f, _g;
                    try {
                        if (visitorTracking && visitorTracking.fingerprintResult) {
                            mixpanel.track(eventNameForService, Object.assign(Object.assign({}, paramsForService), { ipAddress: visitorTracking.fingerprintResult.ip, timeZone: (_a = visitorTracking.fingerprintResult.ipLocation) === null || _a === void 0 ? void 0 : _a.timezone, location: {
                                    city: (_b = visitorTracking.fingerprintResult.ipLocation) === null || _b === void 0 ? void 0 : _b.city,
                                    country: (_d = (_c = visitorTracking.fingerprintResult.ipLocation) === null || _c === void 0 ? void 0 : _c.country) === null || _d === void 0 ? void 0 : _d.code,
                                    latitude: (_e = visitorTracking.fingerprintResult.ipLocation) === null || _e === void 0 ? void 0 : _e.latitude,
                                    longitude: (_f = visitorTracking.fingerprintResult.ipLocation) === null || _f === void 0 ? void 0 : _f.longitude,
                                    postalCode: (_g = visitorTracking.fingerprintResult.ipLocation) === null || _g === void 0 ? void 0 : _g.postalCode, // The postal code or zip code of the endpoint.
                                } }), {
                                send_immediately: options === null || options === void 0 ? void 0 : options.sendImmediately,
                            }, (response) => {
                                if (response === 0 || (response === null || response === void 0 ? void 0 : response.status) === 0) {
                                    reject(new Error('Mixpanel event not sent'));
                                }
                                else {
                                    resolve({ eventName: eventNameForService, eventParams: paramsForService });
                                }
                            });
                        }
                        else {
                            mixpanel.track(eventNameForService, paramsForService, {
                                send_immediately: options === null || options === void 0 ? void 0 : options.sendImmediately,
                            }, (response) => {
                                if (response === 0 || (response === null || response === void 0 ? void 0 : response.status) === 0) {
                                    reject(new Error('Mixpanel event not sent'));
                                }
                                else {
                                    resolve({ eventName: eventNameForService, eventParams: paramsForService });
                                }
                            });
                        }
                    }
                    catch (e) {
                        reject(e);
                    }
                });
            }
            else {
                console.error('mixpanel not initialized');
            }
        }
        catch (e) {
            console.error(`Error sending event (${eventNameForService}) to Mixpanel: ${e.message}`);
        }
    });
}
/**
 *  Logs to mixpanel if in prod, but warns to the console if not.
 *  @param msg a description of the event to be logged
 *  @param obj an optional object of related data to log with the message
 *  @param eventName an optional name for the event, defaults to 'warning'
 * */
function warn(msg, obj, eventName = AnalyticsTrackingEvent.WARNING) {
    if (__PROD__) {
        track(eventName, obj || {});
    }
    else if (obj) {
        console.warn(msg, obj);
    }
    else {
        console.warn(msg);
    }
}
/**
 *  Logs to mixpanel if in prod, but errors in the console if not.
 *  @param msg a description of the event to be logged
 *  @param obj an optional object of related data to log with the message
 *  @param eventName an optional name for the Mixpanel event, defaults to 'error'
 * */
function error(msg, obj, eventName = AnalyticsTrackingEvent.ERROR) {
    if (__PROD__) {
        track(eventName, obj ? Object.assign(Object.assign({}, obj), { message: msg }) : { message: msg });
    }
    else if (obj) {
        console.error(msg, obj);
    }
    else {
        console.error(msg);
    }
}
/**
 * This function should be called when the application encounters an untenable state and cannot continue.  It will throw
 * an `Error` with the provided message and log the provided metadata object to the console if provided.  If in production,
 * it will trigger a Mixpanel event for the error and supply the provided metadata.
 *
 * @param msg a description of the error or unhandleable event that occurred
 * @param obj an optional object of related data to log with the message
 * @param eventName an optional name for the Mixpanel event, defaults to 'return panic'
 */
function panic(msg, obj, eventName = AnalyticsTrackingEvent.RETURN_PANIC) {
    if (__PROD__) {
        track(eventName, obj ? Object.assign(Object.assign({}, obj), { msg }) : { msg });
    }
    else if (obj) {
        console.error(msg, obj);
    }
    throw new Error(msg);
}
const getMixpanelApiHost = () => {
    // Use process.env.NODE_ENV for compatibility with Jest/Node environments
    // In Vite environments, import.meta.env.MODE would be available but we prioritize process.env.NODE_ENV
    const nodeEnv = process.env.NODE_ENV;
    if (nodeEnv === 'development') {
        return undefined; // will default to normal mixpanel host
    }
    return window.location.origin;
};
function initializeMixpanel(domainOverride) {
    if (__PROD__) {
        mixpanel.init('5f5add70c60a83f55ad7ba6d75faf5db', {
            autocapture: {
                pageview: 'full-url',
                input: true,
                submit: true,
                capture_text_content: false,
                click: false,
                scroll: false,
            },
            ignore_dnt: true,
            api_host: domainOverride || getMixpanelApiHost(),
            // Use sendBeacon for reliability during page transitions
            transport: 'sendBeacon',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        });
    }
    else {
        mixpanel.init('cde1743169537cd47bc24fe7f0ee14b0', {
            autocapture: {
                pageview: 'full-url',
                input: true,
                submit: true,
                capture_text_content: false,
                click: false,
                scroll: false,
            },
            ignore_dnt: true,
            api_host: domainOverride || getMixpanelApiHost(),
            // Use sendBeacon for reliability during page transitions
            transport: 'sendBeacon',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        });
    }
    isInitialized = true;
}
function isMixpanelInitialized() {
    return isInitialized;
}
function setMixpanelUserIdentity(visitorTracking, user) {
    if (user) {
        const { given_name: firstName, family_name: lastName, email, userId } = user;
        mixpanel.identify(userId);
        mixpanel.register({
            FirstName: firstName,
            LastName: lastName,
            Email: email,
            UniqueVisitorId: visitorTracking === null || visitorTracking === void 0 ? void 0 : visitorTracking.uniqueVisitorId,
        });
        mixpanel.people.set({
            $first_name: firstName,
            $last_name: lastName,
            $email: email,
            gender: user.genderPredicted || user.gender,
        });
    }
    else if (visitorTracking === null || visitorTracking === void 0 ? void 0 : visitorTracking.uniqueVisitorId) {
        mixpanel.identify(visitorTracking === null || visitorTracking === void 0 ? void 0 : visitorTracking.uniqueVisitorId);
    }
}
/**
 * Used to mark potentially dead code for future deletion. When code could potentially be dead, but you aren't sure,
 * put a tombstone in it and check the logs if it was ever hit. This helps make dead code deletion safer.
 * Idea taken from https://www.bugsnag.com/blog/javascript-refactoring-with-bugsnag-and-tombstones
 *
 * @param msg a custom message for mixpanel, should include the file and line (e.g. operations.ts:113)
 * @param obj an optional object to log with the message.
 */
function tombstone(msg, obj = {}) {
    track(`tombstone: ${msg}`, obj);
}
function trackRouteChange(previousLocation, nextLocation) {
    const params = queryString.parse(nextLocation.search, {
        ignoreQueryPrefix: true,
        arrayLimit: 100,
    });
    track(AnalyticsTrackingEvent.PAGE_VIEW, Object.assign(Object.assign({}, params), { currentUrl: nextLocation.pathname, previousUrl: previousLocation.pathname }));
}
const trackError = (url, err) => {
    track(AnalyticsTrackingEvent.ERROR_PAGE, {
        url,
        message: err.message,
        backtrace: err.stack || '',
        lineNumber: err.lineNumber,
    });
};

export { deferredPromise, error, initializeMixpanel, isMixpanelInitialized, panic, setCurrentVisitorTracking, setMixpanelUserIdentity, tombstone, track, trackError, trackRouteChange, warn };
//# sourceMappingURL=AnalyticsService.js.map
